import {
  Avatar,
  DataTableColumn,
  FlexBox,
  Icon,
  IconButton,
  Text,
  Timestamp,
  Tooltip,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { conversationChannelAttributes } from 'lib-shared-legacy-types';
import { ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  UnbanUserModal,
  UnbanUserModalOnCloseEvent,
} from 'components/shared/unbanUserModal/unbanUserModal';
import { BansByUserResponseDataBans, UserBan } from 'hooks/useBans/types';

import rowStyles from '../bansRow.module.scss';

const DELAY_FETCH = 1000;

// Table column components
// -----------------------
const renderContact = ({ user }: BansByUserResponseDataBans): ReactNode => (
  <FlexBox alignItems="center">
    <Avatar className={rowStyles.userAvatar} user={user} />

    <div className={rowStyles.userInfo}>
      <div className={rowStyles.userName}>{user.name}</div>
      {user.email && <div className={rowStyles.userEmail}>{user.email}</div>}
    </div>
  </FlexBox>
);

const renderBannedChannelsIcons = ({
  bans,
}: BansByUserResponseDataBans): ReactNode => (
  <FlexBox flexWrap="wrap">
    {bans.map((ban) => {
      const { id, channel } = ban;
      const channelAttributes = conversationChannelAttributes.get(channel);
      const iconName = channelAttributes?.icon || 'question';
      const tooltipContent = channelAttributes?.prettyName || 'Unknown channel';

      return (
        <Tooltip key={id} content={tooltipContent} dark>
          <FlexBox
            className={classnames(rowStyles.channel, {
              [rowStyles.banned]: true,
            })}
            alignItems="center"
            justifyContent="center"
          >
            <Icon name={iconName} />
          </FlexBox>
        </Tooltip>
      );
    })}
  </FlexBox>
);

const renderLastBan = ({ bans }: BansByUserResponseDataBans): ReactNode => (
  <div className={rowStyles.lastBanInfo}>
    {bans[0]?.banned_at && (
      <div className={rowStyles.lastBanInfoDate}>
        <Timestamp relative time={bans[0].banned_at} />
      </div>
    )}

    {bans[0]?.banning_agent && (
      <div hidden className={rowStyles.lastBanInfoAgent}>
        by {bans[0].banning_agent.name}
      </div>
    )}
  </div>
);

const renderUnbanButtonWithRefreshItems = (refreshItems: () => void) =>
  function RenderUnbanButton({
    bans,
    user,
  }: BansByUserResponseDataBans): ReactNode {
    const [isUnbanModalOpen, setIsUnbanModalOpen] = useState(false);

    // checking if there is a ban without an unban, to see if there are any active bans
    const hasActiveBan = !bans.reduce(
      (acc, { unbanned_at }) => acc && !!unbanned_at,
      true,
    );

    return (
      hasActiveBan && (
        <>
          <Tooltip content="Unban" dark>
            <IconButton
              variant="tertiary"
              icon="undo"
              onClick={() => setIsUnbanModalOpen(true)}
            />
          </Tooltip>
          <UnbanUserModal
            user={user}
            // NOTE: for now we only ban on chat - we'll need to rethink this when we have bans on all channels
            channel="widget_chat"
            isOpen={isUnbanModalOpen}
            onClose={(event: UnbanUserModalOnCloseEvent) => {
              setIsUnbanModalOpen(false);
              if (!event.canceled && event.response) {
                setTimeout(() => {
                  refreshItems();
                }, DELAY_FETCH);
              }
            }}
          />
        </>
      )
    );
  };

// Sub-table column components
// ---------------------------
const renderChannel = ({ channel }: UserBan): ReactNode => {
  const channelIcon = conversationChannelAttributes.get(channel)?.icon;
  return (
    channelIcon && (
      <Icon name={channelIcon} style={{ verticalAlign: 'middle' }} />
    )
  );
};

const renderBannedBy = ({ banning_agent }: UserBan): ReactNode => (
  <Link to={`/contacts/user/${banning_agent.id}`}>{banning_agent.name}</Link>
);

const renderBannedAt = ({ banned_at }: UserBan): ReactNode => (
  <Timestamp relative time={banned_at} />
);

const renderConversation = ({ csid }: UserBan): ReactNode =>
  (csid && <Link to={`/conversation/${csid}`}>#{csid}</Link>) || (
    <Text.Paragraph as="span" subdued>
      n/a
    </Text.Paragraph>
  );

// Columns
// -------
export const tableColumns = (
  refreshItems: () => void,
): DataTableColumn<BansByUserResponseDataBans>[] => [
  {
    label: 'Contact',
    selector: renderContact,
  },
  {
    label: 'Banned on',
    style: { width: '45%' },
    selector: renderBannedChannelsIcons,
  },
  {
    label: 'Last ban',
    style: { width: '15%' },
    selector: renderLastBan,
  },
  {
    style: { width: 1 },
    selector: renderUnbanButtonWithRefreshItems(refreshItems),
  },
];

export const subTableColumns: DataTableColumn<UserBan>[] = [
  { style: { width: 1 }, selector: renderChannel },
  {
    label: 'Banned by',
    style: { width: '20%' },
    selector: renderBannedBy,
  },
  {
    label: 'Banned at',
    style: { width: '18%' },
    selector: renderBannedAt,
  },
  { label: 'Conversation', selector: renderConversation },
];
